import React, { useState, useEffect } from "react";
import {
    Row,
    Col,
    Card,
    Typography,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    Upload,
    Divider,
    Spin,
    Empty,
    Button,
    Tabs,
    List,
    Avatar,
    Space,
    Switch,
    Image
} from "antd";
import Notify from "../util/Notify";
import {
    EditOutlined,
    PlusOutlined,
    UserOutlined,
    PhoneOutlined,
    MailOutlined,
    CalendarOutlined,
    UploadOutlined,
    HomeOutlined,
    LockOutlined,
    EnvironmentOutlined,
    IdcardOutlined,
    DeleteOutlined
} from "@ant-design/icons";
import UserServ from "../service/UserServ";
import GlobalButton from "../util/GlobalButton";
import { getImageUrl } from "./SafeImage";
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../util/canvasUtils';
import "../css/Profile.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import ProfileSkeleton from "./skeletons/ProfileSkeleton";
import GlobalHeader from "../util/GlobalHeader";
import { usePreference } from "../context/PreferenceContext";
import GlobalMap from "../util/GlobalMap";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const UnifiedProfile = ({ role }) => {
    const { t, formatPrice } = usePreference();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get("tab") || "personal";

    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
    const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [editingAddressIndex, setEditingAddressIndex] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [mapLocation, setMapLocation] = useState(null);
    const [mapAddress, setMapAddress] = useState("");

    const handleTabChange = (key) => {
        setSearchParams({ tab: key });
    };

    const [fileToUpload, setFileToUpload] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Crop States
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isCropModalOpen, setIsCropModalOpen] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);

    const [infoForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [privacyForm] = Form.useForm();
    const [notifyForm] = Form.useForm();
    const [addressForm] = Form.useForm();

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const res = await UserServ.getProfile();
            setUser(res.data);
            infoForm.setFieldsValue(res.data);
        } catch (err) {
            Notify.error(err, t('error_loading_profile'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfile();
    }, []);

    const handleInfoUpdate = async (values) => {
        try {
            const updatePayload = {
                firstName: values.firstName,
                lastName: values.lastName,
                phone: values.phone
            };
            await UserServ.updateProfile(updatePayload);
            Notify.success(t('profile_updated_success'));
            setIsInfoModalOpen(false);
            window.dispatchEvent(new Event('profile-updated'));
            fetchProfile();
        } catch (error) {
            Notify.error(error, t('error_updating_profile'));
        }
    };

    const beforeUpload = (file) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
        if (!isJpgOrPng) {
            Notify.error(t('invalid_file'), t('invalid_file_desc'));
            return false;
        }
        const isLt2M = file.size / 1024 / 1024 < 5; // Increased to 5MB for cropping
        if (!isLt2M) {
            Notify.error(t('file_too_large'), t('file_too_large_desc'));
            return false;
        }

        const reader = new FileReader();
        reader.addEventListener('load', () => {
            setImageToCrop(reader.result);
            setIsCropModalOpen(true);
        });
        reader.readAsDataURL(file);
        return false;
    };

    const onCropComplete = (croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleCropSave = async () => {
        try {
            const { file, url } = await getCroppedImg(imageToCrop, croppedAreaPixels);
            setFileToUpload(file);
            setPreviewImage(url);
            setIsCropModalOpen(false);
        } catch (e) {
            Notify.error(e, t('crop_failed'));
        }
    };

    const handleImageUpload = async () => {
        if (!fileToUpload) return;
        setUploading(true);
        const formData = new FormData();
        formData.append("image", fileToUpload);

        try {
            const res = await UserServ.uploadProfileImage(formData);
            await UserServ.updateProfile({ profilePhoto: res.imageUrl });
            Notify.success(t('profile_image_updated'));
            setIsImageModalOpen(false);
            setFileToUpload(null);
            setPreviewImage(null);
            window.dispatchEvent(new Event('profile-updated'));
            fetchProfile();
        } catch (err) {
            Notify.error(err, t('image_upload_failed'));
        } finally {
            setUploading(false);
        }
    };

    const handlePasswordUpdate = async (values) => {
        try {
            setActionLoading(true);
            await UserServ.changePassword({
                oldPassword: values.oldPassword,
                newPassword: values.newPassword
            });
            Notify.success(t('password_updated_success'));
            setIsPasswordModalOpen(false);
            passwordForm.resetFields();
        } catch (error) {
            Notify.error(error, t('error_updating_password'));
        } finally {
            setActionLoading(false);
        }
    };

    const handlePrivacyUpdate = async (values) => {
        try {
            setActionLoading(true);
            await UserServ.updatePrivacy(values);
            Notify.success(t('privacy_settings_updated'));
            setIsPrivacyModalOpen(false);
            fetchProfile();
        } catch (error) {
            Notify.error(error, t('error_updating_privacy'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleNotifyUpdate = async (values) => {
        try {
            setActionLoading(true);
            await UserServ.updateNotifications(values);
            Notify.success(t('notify_settings_updated'));
            setIsNotifyModalOpen(false);
            fetchProfile();
        } catch (error) {
            Notify.error(error, t('error_updating_notify'));
        } finally {
            setActionLoading(false);
        }
    };

    const openPrivacyModal = () => {
        privacyForm.setFieldsValue(user.privacySettings || {});
        setIsPrivacyModalOpen(true);
    };

    const openNotifyModal = () => {
        notifyForm.setFieldsValue(user.notificationSettings || {});
        setIsNotifyModalOpen(true);
    };

    const handleAddAddress = () => {
        setEditingAddressIndex(null);
        addressForm.resetFields();
        setMapLocation(null);
        setMapAddress("");
        setIsAddressModalOpen(true);
    };

    const handleEditAddress = (addr, index) => {
        setEditingAddressIndex(index);
        addressForm.setFieldsValue({
            ...addr,
            addressType: addr.addressType?.toLowerCase()
        });
        if (addr.latitude && addr.longitude) {
            setMapLocation({ lat: addr.latitude, lng: addr.longitude });
        } else {
            setMapLocation(null);
        }
        setMapAddress(addr.street || "");
        setIsAddressModalOpen(true);
    };

    const handleDeleteAddress = async (index) => {
        try {
            const newAddresses = user.address.filter((_, i) => i !== index);
            await UserServ.updateProfile({ address: newAddresses });
            Notify.success(t('address_removed_success'));
            fetchProfile();
        } catch (error) {
            Notify.error(error, t('error_removing_address'));
        }
    };

    const handleAddressSave = async (values) => {
        try {
            const finalValues = {
                ...values,
                latitude: mapLocation?.lat,
                longitude: mapLocation?.lng
            };
            let newAddresses = [...(user.address || [])];
            if (editingAddressIndex !== null) {
                newAddresses[editingAddressIndex] = { ...newAddresses[editingAddressIndex], ...finalValues };
            } else {
                newAddresses.push(finalValues);
            }

            newAddresses = newAddresses.map(addr => ({
                ...addr,
                addressType: addr.addressType?.toLowerCase(),
                isDefault: addr.isDefault || false
            }));

            await UserServ.updateProfile({ address: newAddresses });
            Notify.success(t('addresses_updated_success'));
            setIsAddressModalOpen(false);
            fetchProfile();
        } catch (error) {
            Notify.error(error, t('failed_save_address'));
        }
    };

    const handleLocationSelect = (loc) => {
        setMapLocation({ lat: loc.latitude, lng: loc.longitude });
        setMapAddress(loc.address);
        addressForm.setFieldsValue({
            street: loc.address,
            city: loc.city,
            district: loc.district,
            pincode: loc.zipCode
        });
    };

    const ProfileHeader = () => (
        <Card className="profile-premium-card pcu-margin-bottom-32 pcu-radius-24 pcu-overflow-hidden">
            <div className="profile-premium-cover">
                <div className="cover-overlay"></div>
                <div className="header-actions">
                    <GlobalButton
                        onClick={() => setIsInfoModalOpen(true)}
                        icon={<EditOutlined />}
                        className="premium-edit-btn"
                    />
                </div>
            </div>
            <div className="profile-premium-header-content">
                <div className="profile-avatar-section">
                    <div className="premium-avatar-wrapper">
                        <Image
                            width={110}
                            height={110}
                            src={getImageUrl(user.profilePhoto)}
                            fallback={getImageUrl(null)}
                            className="pcu-avatar-premium-img"
                            preview={{
                                mask: <div className="avatar-preview-mask"><UserOutlined /></div>
                            }}
                        />
                        <Button
                            type="primary"
                            shape="circle"
                            icon={<UploadOutlined />}
                            className="premium-upload-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsImageModalOpen(true);
                            }}
                        />
                    </div>
                </div>

                <div className="profile-premium-info-main">
                    <div className="user-identity-row">
                        <Title level={1} className="premium-user-name">
                            {user.firstName} {user.lastName}
                        </Title>
                        <div className="identity-details-badges">
                            <span className="premium-role-badge">
                                {t(user.role?.toLowerCase())}
                            </span>
                            <span className={`premium-status-indicator ${user.isActive ? 'active' : 'inactive'}`}>
                                {user.isActive ? t('active_status') : t('inactive_status')}
                            </span>
                            <span className="premium-user-id">
                                #{user.userId}
                            </span>
                        </div>
                    </div>

                    <div className="profile-internal-stats-row">
                        <div className="internal-stat-item">
                            <span className="stat-label">{t('credits')}</span>
                            <span className="stat-value">{formatPrice(user.credits || 0)}</span>
                        </div>
                        <div className="internal-stat-divider"></div>
                        <div className="internal-stat-item">
                            <span className="stat-label">{t('addresses')}</span>
                            <span className="stat-value">{user.address?.length || 0}</span>
                        </div>
                        <div className="internal-stat-divider"></div>
                        <div className="internal-stat-item">
                            <span className="stat-label">{t('member_since')}</span>
                            <span className="stat-value">{moment(user.createdAt).format('MMMM YYYY')}</span>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );

    const PersonalInfo = () => (
        <Row gutter={[24, 24]} style={{ marginTop: "20px" }}>
            <Col xs={24} md={12}>
                <div className="info-group">
                    <Text type="secondary" className="pcu-text-12 pcu-text-uppercase pcu-letter-spacing-1">{t('full_name')}</Text>
                    <div className="pcu-margin-top-4">
                        <Text strong className="pcu-text-16">{user.firstName} {user.lastName}</Text>
                    </div>
                </div>
            </Col>
            <Col xs={24} md={12}>
                <div className="info-group">
                    <Text type="secondary" className="pcu-text-12 pcu-text-uppercase pcu-letter-spacing-1">{t('email_address')}</Text>
                    <div className="pcu-margin-top-4 pcu-flex pcu-flex-center-y pcu-gap-8">
                        <MailOutlined className="pcu-text-orange" style={{ marginTop: "-4px" }} />
                        <Text strong className="pcu-text-16">{user.email}</Text>
                    </div>
                </div>
            </Col>
            <Col xs={24} md={12}>
                <div className="info-group">
                    <Text type="secondary" className="pcu-text-12 pcu-text-uppercase pcu-letter-spacing-1">{t('phone_number')}</Text>
                    <div className="pcu-margin-top-4 pcu-flex pcu-flex-center-y pcu-gap-8">
                        <PhoneOutlined className="pcu-text-orange" style={{ marginTop: "-4px" }} />
                        <Text strong className="pcu-text-16">{user.phone || t('not_provided')}</Text>
                    </div>
                </div>
            </Col>
            <Col xs={24} md={12}>
                <div className="info-group">
                    <Text type="secondary" className="pcu-text-12 pcu-text-uppercase pcu-letter-spacing-1">{t('joined_nearzon')}</Text>
                    <div className="pcu-margin-top-4 pcu-flex pcu-flex-center-y pcu-gap-8">
                        <CalendarOutlined className="pcu-text-orange" style={{ marginTop: "-4px" }} />
                        <Text strong className="pcu-text-16">{user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : t('na')}</Text>
                    </div>
                </div>
            </Col>
            <Col xs={24} md={12}>
                <div className="info-group">
                    <Text type="secondary" className="pcu-font-12 pcu-text-uppercase pcu-letter-spacing-1">{t('credit_balance')}</Text>
                    <div className="pcu-margin-top-4 pcu-flex pcu-flex-center-y pcu-gap-8">
                        <div className="pcu-bg-orange-light pcu-padding-4 pcu-radius-4"><Text strong className="pcu-color-orange pcu-font-16">{formatPrice(user.credits || 0)}</Text></div>
                    </div>
                </div>
            </Col>
        </Row>
    );

    const MyAddresses = () => (
        <div>
            <div className="pcu-flex pcu-justify-between pcu-items-center pcu-margin-bottom-20">
                <Title level={5} style={{ marginTop: "10px" }} className="pcu-margin-0">{t('saved_addresses')}</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAddress} className="pcu-bg-orange pcu-border-orange">
                    {t('add_address_btn')}
                </Button>
            </div>
            <Row gutter={[16, 16]}>
                {(!user.address || user.address.length === 0) ? (
                    <Col span={24}>
                        <Empty description={t('no_addresses_found')} />
                    </Col>
                ) : (
                    user.address.map((addr, idx) => (
                        <Col xs={24} sm={12} key={idx}>
                            <Card
                                size="small"
                                className="pcu-radius-12 pcu-border-light"
                                hoverable
                                title={
                                    <Space>
                                        <EnvironmentOutlined className="pcu-text-orange" />
                                        <Text strong>
                                            {addr.addressType === 'other'
                                                ? (addr.otherAddressType || t('other_label')).toUpperCase()
                                                : (t(`${addr.addressType?.toLowerCase()}_label`) || t('address')).toUpperCase()}
                                        </Text>
                                        {addr.isDefault && <Tag color="blue">{t('default')}</Tag>}
                                    </Space>
                                }
                                actions={[
                                    <EditOutlined key="edit" onClick={() => handleEditAddress(addr, idx)} />,
                                    <DeleteOutlined key="delete" className="pcu-text-error" onClick={() => handleDeleteAddress(idx)} />
                                ]}
                            >
                                <Paragraph className="pcu-margin-0 pcu-text-muted pcu-min-h-60">
                                    {addr.street},<br />
                                    {addr.city || addr.taluk}, {addr.district}<br />
                                    {addr.pincode}
                                </Paragraph>
                            </Card>
                        </Col>
                    ))
                )}
            </Row>
        </div>
    );

    const ProfileSettings = () => (
        <div>
            <Title level={5} style={{ marginTop: "10px" }} className="pcu-margin-bottom-20">{t('security_settings')}</Title>
            <List
                itemLayout="horizontal"
                dataSource={[
                    { title: t('change_password'), desc: t('change_password_desc'), icon: <LockOutlined />, key: 'password' },
                    { title: t('privacy_settings'), desc: t('privacy_settings_desc'), icon: <UserOutlined />, key: 'privacy' },
                    { title: t('notification_settings'), desc: t('notification_settings_desc'), icon: <MailOutlined />, key: 'notifications' }
                ]}
                renderItem={item => (
                    <List.Item actions={[
                        <Button
                            type="link"
                            onClick={() => {
                                if (item.key === 'password') setIsPasswordModalOpen(true);
                                if (item.key === 'privacy') openPrivacyModal();
                                if (item.key === 'notifications') openNotifyModal();
                            }}
                            className="pcu-font-500 pcu-text-orange"
                        >
                            {t('update')}
                        </Button>
                    ]}>
                        <List.Item.Meta
                            avatar={<Avatar icon={item.icon} className="pcu-bg-orange-light pcu-text-orange" />}
                            title={<Text strong>{item.title}</Text>}
                            description={item.desc}
                        />
                    </List.Item>
                )}
            />
        </div>
    );


    return (
        <div className="unified-profile-page pcu-max-w-900 pcu-margin-x-auto pcu-padding-20">
            <GlobalHeader title={t('profile_settings')}>

                {loading ? (
                    <ProfileSkeleton />
                ) : !user ? (
                    <div className="pcu-padding-y-40 pcu-w-100">
                        <Card className="pcu-text-center pcu-radius-12">
                            <Empty description={t('no_profile_data')} />
                            <Button onClick={fetchProfile} className="pcu-margin-top-16">{t('retry')}</Button>
                        </Card>
                    </div>
                ) : (
                    <>
                        <ProfileHeader />

                        <Card className="pcu-radius-16 pcu-shadow-sm">
                            <Tabs
                                activeKey={activeTab}
                                onChange={handleTabChange}
                                items={[
                                    {
                                        key: 'personal',
                                        label: <span className="pcu-padding-x-10"><UserOutlined /> {t('personal_info_tab')}</span>,
                                        children: <div className="pcu-padding-y-24"><PersonalInfo /></div>,
                                    },
                                    {
                                        key: 'address',
                                        label: <span className="pcu-padding-x-10"><HomeOutlined /> {t('my_addresses_tab')}</span>,
                                        children: <div className="pcu-padding-y-24"><MyAddresses /></div>,
                                    },
                                    {
                                        key: 'settings',
                                        label: <span className="pcu-padding-x-10"><LockOutlined /> {t('profile_settings_tab')}</span>,
                                        children: <div className="pcu-padding-y-24"><ProfileSettings /></div>,
                                    }
                                ]}
                                className="profile-tabs"
                                animated={{ inkBar: true, tabPane: true }}
                            />
                        </Card>
                    </>
                )}


                <Modal
                    open={isInfoModalOpen}
                    onCancel={() => setIsInfoModalOpen(false)}
                    footer={null}
                    width={550}
                    centered
                    closable={false}
                    className="ss-modal"
                    bodyStyle={{ padding: 0 }}
                    maskStyle={{ backdropFilter: 'blur(3px)' }}
                    style={{ borderRadius: 12, overflow: 'hidden' }}
                    styles={{ content: { padding: 0, borderRadius: 12, overflow: 'hidden' }, body: { padding: 0 } }}
                >
                    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
                        <div style={{
                            padding: '12px 16px',
                            background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#1e293b' }}>
                                    {t('edit_personal_info')}
                                </h2>
                                <p style={{ margin: '2px 0 0', fontSize: 10, color: '#64748b', fontWeight: 600 }}>
                                    Update your personal details
                                </p>
                            </div>
                            <button
                                onClick={() => setIsInfoModalOpen(false)}
                                style={{ background: '#fff', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 12, width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                            >
                                <span style={{ fontSize: 12 }}>×</span>
                            </button>
                        </div>

                        <div style={{ padding: '16px 20px', maxHeight: '70vh', overflowY: 'auto' }}>
                            <Form form={infoForm} layout="vertical" onFinish={handleInfoUpdate} requiredMark={false} size="small">
                                <Row gutter={16}>
                                    <Col xs={24} md={8} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: 12 }}>
                                            {user?.profilePhoto ? (
                                                <img src={getImageUrl(user.profilePhoto)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <UserOutlined style={{ fontSize: 24, color: '#94a3b8' }} />
                                            )}
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <Form.Item name="profilePhoto" style={{ marginBottom: 0 }}>
                                                <Upload beforeUpload={beforeUpload} showUploadList={false} accept="image/*">
                                                    <Button icon={<UploadOutlined />} size="small" style={{ borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                                                        Upload Photo
                                                    </Button>
                                                </Upload>
                                            </Form.Item>
                                            <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 4 }}>Recommended: 200x200px</div>
                                        </div>
                                    </Col>

                                    <Col xs={24} md={16}>
                                        <Row gutter={12}>
                                            <Col xs={24}>
                                                <Form.Item
                                                    name="firstName"
                                                    label={<span style={{ fontSize: 9, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 2 }}>{t('first_name')} <span style={{ color: '#ef4444' }}>*</span></span>}
                                                    rules={[{ required: true, message: t('enter_first_name') }]}
                                                    style={{ marginBottom: 8 }}
                                                >
                                                    <Input placeholder={t('first_name')} style={{ borderRadius: 4 }} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24}>
                                                <Form.Item
                                                    name="lastName"
                                                    label={<span style={{ fontSize: 9, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 2 }}>{t('last_name')} <span style={{ color: '#ef4444' }}>*</span></span>}
                                                    rules={[{ required: true, message: t('enter_last_name') }]}
                                                    style={{ marginBottom: 8 }}
                                                >
                                                    <Input placeholder={t('last_name')} style={{ borderRadius: 4 }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                <Row gutter={12}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            name="phone"
                                            label={<span style={{ fontSize: 9, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 2 }}>{t('phone_number')} <span style={{ color: '#ef4444' }}>*</span></span>}
                                            rules={[{ required: true }]}
                                            style={{ marginBottom: 8 }}
                                        >
                                            <Input placeholder={t('phone_number')} disabled style={{ borderRadius: 4 }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label={<span style={{ fontSize: 9, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 2 }}>{t('email_address')}</span>}
                                            style={{ marginBottom: 8 }}
                                        >
                                            <Input value={user?.email} disabled style={{ borderRadius: 4 }} />
                                            <Text type="secondary" style={{ fontSize: 9, marginTop: 4, display: 'block' }}>{t('email_unchangeable')}</Text>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Col xs={24} style={{ marginTop: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <span style={{ fontSize: 9, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 0 }}>Saved Addresses</span>
                                        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={handleAddAddress} style={{ borderRadius: 4, fontSize: 10, padding: '0 8px', height: 24, color: '#6366f1', borderColor: '#c7d2fe', background: '#eef2ff' }}>
                                            Add Address
                                        </Button>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                                        {(user?.address && user.address.length > 0) ? user.address.map((addr, idx) => (
                                            <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 8, position: 'relative', background: '#f8fafc' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                                                    <EnvironmentOutlined style={{ color: '#6366f1', fontSize: 10 }} />
                                                    <span style={{ fontSize: 9, fontWeight: 700, color: '#1e293b', textTransform: 'uppercase' }}>{addr.addressType === 'other' ? addr.otherAddressType : addr.addressType || 'Address'}</span>
                                                </div>
                                                <p style={{ margin: 0, fontSize: 10, color: '#64748b', lineHeight: 1.3 }}>
                                                    {[addr.street, addr.city || addr.taluk, addr.district, addr.pincode].filter(Boolean).join(", ")}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditAddress(addr, idx)}
                                                    style={{ position: 'absolute', bottom: 8, right: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', color: '#6366f1', padding: 4, display: 'flex', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                                                >
                                                    <EditOutlined style={{ fontSize: 10 }} />
                                                </button>
                                            </div>
                                        )) : (
                                            <div style={{ gridColumn: '1 / -1', padding: '12px', textAlign: 'center', background: '#f8fafc', borderRadius: 6, border: '1px dashed #cbd5e1', fontSize: 10, color: '#94a3b8' }}>
                                                No addresses saved.
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Form>
                        </div>

                        <div style={{ padding: '12px 16px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                            <button
                                onClick={() => setIsInfoModalOpen(false)}
                                style={{
                                    padding: '6px 16px', borderRadius: 6, border: '1px solid #e2e8f0',
                                    background: '#fff', color: '#64748b', fontSize: 11, fontWeight: 700,
                                    cursor: 'pointer', transition: 'all 0.2s'
                                }}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={() => infoForm.submit()}
                                style={{
                                    padding: '6px 20px', borderRadius: 6, border: 'none',
                                    background: '#4F46E5', color: '#fff', fontSize: 11, fontWeight: 700,
                                    cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(79, 70, 229, 0.2)'
                                }}
                            >
                                Update Profile
                            </button>
                        </div>
                    </div>
                </Modal>

                <Modal
                    title={t('update_profile_pic')}
                    open={isImageModalOpen}
                    onCancel={() => setIsImageModalOpen(false)}
                    footer={null}
                    centered
                    width={400}
                >
                    <div className="pcu-text-center pcu-padding-y-20">
                        <div className="pcu-margin-bottom-24">
                            <Avatar
                                size={160}
                                src={previewImage || getImageUrl(user?.profilePhoto)}
                                icon={<UserOutlined />}
                                className="pcu-avatar-large"
                            />
                        </div>

                        <Upload beforeUpload={beforeUpload} showUploadList={false}>
                            <Button icon={<PlusOutlined />} className="pcu-margin-bottom-24">{t('select_new_photo')}</Button>
                        </Upload>

                        <Button
                            loading={uploading}
                            onClick={handleImageUpload}
                            type="primary"
                            block
                            size="large"
                            className="pcu-bg-orange pcu-border-orange pcu-radius-8"
                            disabled={!fileToUpload}
                        >
                            {t('upload_save')}
                        </Button>
                    </div>
                </Modal>

                <Modal
                    title={t('crop_image')}
                    open={isCropModalOpen}
                    onCancel={() => setIsCropModalOpen(false)}
                    onOk={handleCropSave}
                    width={600}
                    centered
                    destroyOnClose
                >
                    <div className="crop-container" style={{ position: 'relative', width: '100%', height: 400, background: '#333' }}>
                        <Cropper
                            image={imageToCrop}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                        />
                    </div>
                    <div className="pcu-padding-y-16">
                        <Text>{t('zoom_label')}</Text>
                        <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            aria-labelledby="Zoom"
                            onChange={(e) => setZoom(e.target.value)}
                            className="zoom-range"
                            style={{ width: '100%', marginTop: 8 }}
                        />
                    </div>
                </Modal>

                <Modal
                    title={t('change_password')}
                    open={isPasswordModalOpen}
                    onCancel={() => setIsPasswordModalOpen(false)}
                    onOk={() => passwordForm.submit()}
                    confirmLoading={actionLoading}
                    okText={t('change_password')}
                    okButtonProps={{ className: "pcu-bg-orange pcu-border-orange" }}
                >
                    <Form form={passwordForm} layout="vertical" onFinish={handlePasswordUpdate} className="pcu-margin-top-15">
                        <Form.Item name="oldPassword" label={t('current_password')} rules={[{ required: true, message: t('please_enter_curr_pass') }]}>
                            <Input.Password placeholder={t('enter_curr_pass')} />
                        </Form.Item>
                        <Form.Item name="newPassword" label={t('new_password')} rules={[{ required: true, min: 6, message: t('pass_min_6') }]}>
                            <Input.Password placeholder={t('enter_new_pass')} />
                        </Form.Item>
                        <Form.Item name="confirmPassword" label={t('confirm_new_password')} dependencies={['newPassword']} rules={[
                            { required: true, message: t('please_confirm_new_pass') },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                                    return Promise.reject(new Error(t('passwords_do_not_match')));
                                },
                            }),
                        ]}>
                            <Input.Password placeholder={t('confirm_new_pass')} />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title={t('privacy_settings')}
                    open={isPrivacyModalOpen}
                    onCancel={() => setIsPrivacyModalOpen(false)}
                    onOk={() => privacyForm.submit()}
                    confirmLoading={actionLoading}
                    okButtonProps={{ className: "pcu-bg-orange pcu-border-orange" }}
                >
                    <Form form={privacyForm} layout="horizontal" onFinish={handlePrivacyUpdate} className="pcu-margin-top-20">
                        <Form.Item name="showProfile" label={t('show_profile_public')} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item name="showEmail" label={t('show_email_address')} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item name="showPhone" label={t('show_phone_number')} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title={t('notification_settings')}
                    open={isNotifyModalOpen}
                    onCancel={() => setIsNotifyModalOpen(false)}
                    onOk={() => notifyForm.submit()}
                    confirmLoading={actionLoading}
                    okButtonProps={{ className: "pcu-bg-orange pcu-border-orange" }}
                >
                    <Form form={notifyForm} layout="horizontal" onFinish={handleNotifyUpdate} className="pcu-margin-top-20">
                        <Form.Item name="emailNotifications" label={t('email_notifications')} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item name="orderUpdates" label={t('order_status_updates')} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item name="promotionalEmails" label={t('promotional_emails')} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title={editingAddressIndex !== null ? t('edit_address') : t('add_new_address')}
                    open={isAddressModalOpen}
                    onCancel={() => setIsAddressModalOpen(false)}
                    onOk={() => addressForm.submit()}
                    destroyOnClose
                    width={700}
                    className="address-modal-premium"
                >
                    <div className="pcu-padding-bottom-20">
                        <GlobalMap
                            height="260px"
                            markerPosition={mapLocation}
                            address={mapAddress}
                            onLocationSelect={handleLocationSelect}
                        />
                    </div>
                    <Form form={addressForm} layout="vertical" onFinish={handleAddressSave}>
                        <Form.Item name="addressType" label={t('address_type')} rules={[{ required: true, message: t('select_addr_type') }]}>
                            <Select placeholder={t('select_addr_type')}>
                                <Option value="home">{t('home_label')}</Option>
                                <Option value="office">{t('office_label')}</Option>
                                <Option value="pickup">{t('pickup_label')}</Option>
                                <Option value="billing">{t('billing_label')}</Option>
                                <Option value="other">{t('other_label')}</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.addressType !== currentValues.addressType}
                        >
                            {({ getFieldValue }) =>
                                getFieldValue('addressType') === 'other' ? (
                                    <Form.Item
                                        name="otherAddressType"
                                        label={t('label_eg_warehouse')}
                                        rules={[{ required: true, message: t('enter_custom_label') }]}
                                    >
                                        <Input placeholder={t('enter_custom_label')} />
                                    </Form.Item>
                                ) : null
                            }
                        </Form.Item>

                        <Form.Item name="street" label={t('street')} rules={[{ required: true, message: t('enter_street') }]}>
                            <Input placeholder={t('enter_street')} />
                        </Form.Item>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="city" label={t('city_taluk')}>
                                    <Input placeholder={t('city_taluk')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="district" label={t('district')}>
                                    <Input placeholder={t('district')} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="pincode" label={t('pincode')} rules={[{ required: true, message: t('enter_pincode') }]}>
                            <Input placeholder={t('pincode')} />
                        </Form.Item>
                    </Form>
                </Modal>
            </GlobalHeader>
        </div >
    );
};

export default UnifiedProfile;
